import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as yup from 'yup'
import { ErrorResponse } from '@ocean/api'
import { usePrevious } from '@ocean/hooks'
import { Mode, Size } from '@ocean/types/dist/appearance'
import { layouts, common, fields, controls } from '@ocean/ui'
import { ErrorUtils } from 'utils'
import { GoToButton } from 'components/common'
import { AnimatedSection, FinalState } from 'modules/Auth/components'
import { AuthFormLayout } from 'modules/Auth/layouts'
import { namespace } from 'modules/Auth/localization'
import authRoutes from 'modules/Auth/routing/routes'
import { ChangePasswordParams, AuthUserParams } from 'modules/Auth/types'
import { NoteWrapper, ErrorWrapper, BackButtonWrapper } from './ChangePasswordForm.styled'

const { Note, Icon } = common
const { Button, common: commonControls } = controls
const { LoadingLayout } = layouts
const { InputField } = fields
const { ErrorMessage } = commonControls

const I18_PATH = `${namespace}:auth.changePassword`

type ChangePasswordFormProps = {
  onSubmit: (formData: ChangePasswordParams, authUserParams: AuthUserParams) => void
  loading?: boolean
  error?: ErrorResponse
  authUserParams: AuthUserParams
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = props => {
  const { onSubmit, loading, error, authUserParams } = props
  const prevLoading = usePrevious(loading)
  const [formikBag, setFormikBag] = useState<FormikHelpers<ChangePasswordParams> | null>(null)
  const [requestSuccessful, setSuccessState] = useState(false)

  useEffect(() => {
    if (prevLoading && !loading && formikBag) {
      formikBag.setSubmitting(false)
      if (!error) {
        setSuccessState(true)
      }
    }
  }, [formikBag, loading, prevLoading, error])

  const { t } = useTranslation()

  const VALIDATION_SCHEMA: yup.SchemaOf<ChangePasswordParams> = yup.object().shape({
    password: yup.string().required(t(`${I18_PATH}.passwordRequired`)),
    repeatPassword: yup
      .string()
      .oneOf([yup.ref('password'), undefined], t(`${I18_PATH}.passwordMatchError`))
      .required(t(`${I18_PATH}.repeatPasswordRequired`)),
  })

  const INITIAL_VALUES: ChangePasswordParams = {
    password: '',
    repeatPassword: '',
  }

  function handleSubmitForm(
    values: ChangePasswordParams,
    actions: FormikHelpers<ChangePasswordParams>
  ) {
    onSubmit(values, authUserParams)
    setFormikBag(actions)
  }

  function renderSuccess() {
    return (
      <FinalState
        title={t(`${I18_PATH}.header1`)}
        description={t(`${I18_PATH}.allSet`)}
        backText={t(`${I18_PATH}.login`)}
        backPath={authRoutes.login}
        dataTest='ChangePasswordSuccess'
      />
    )
  }

  function renderForm() {
    return (
      <>
        <AnimatedSection animationDuration='0.6s'>
          <NoteWrapper>
            <Note
              title={t(`${I18_PATH}.header1`)}
              description={t(`${I18_PATH}.header2`)}
              dataTest='ChangePasswordFormNote'
            />
          </NoteWrapper>
        </AnimatedSection>
        {error && (
          <ErrorWrapper>
            <ErrorMessage
              error={ErrorUtils.getPasswordErrorMessage(error, I18_PATH)}
              dataTest='ChangePasswordErrorMessage'
            />
          </ErrorWrapper>
        )}
        <Formik
          validationSchema={VALIDATION_SCHEMA}
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmitForm}
          validateOnMount
          validateOnBlur
          validateOnChange
        >
          {({ isValid, isSubmitting }) => (
            <LoadingLayout isLoading={loading}>
              <Form>
                <AuthFormLayout>
                  <AnimatedSection animationDuration='0.8s'>
                    <Field
                      title={t(`${I18_PATH}.password`)}
                      name='password'
                      type='password'
                      icon={<Icon icon='lock' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='0.9s'>
                    <Field
                      title={t(`${I18_PATH}.repeatPassword`)}
                      name='repeatPassword'
                      type='password'
                      icon={<Icon icon='lock' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='1s'>
                    <Button
                      fill
                      type='submit'
                      title={t(`${I18_PATH}.savePassword`)}
                      disabled={!isValid || isSubmitting}
                      dataTest='ChangePasswordSubmitButton'
                    />
                  </AnimatedSection>
                </AuthFormLayout>
              </Form>
            </LoadingLayout>
          )}
        </Formik>
      </>
    )
  }

  return (
    <AnimatedSection animationDuration='0.5s'>
      <BackButtonWrapper>
        <GoToButton
          title={t(`${I18_PATH}.backToLogin`)}
          to={authRoutes.login}
          mode={Mode.Dark}
          dataTest='ChangePasswordBackButton'
        />
      </BackButtonWrapper>
      {requestSuccessful ? renderSuccess() : renderForm()}
    </AnimatedSection>
  )
}

export default ChangePasswordForm
